/**
 * TournamentAdminPage.js — Full tournament management (Overview, Teams, Bracket, QR)
 * CSS prefix: ta-
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import DOMPurify from 'dompurify';
import * as api from '../services/api';
import './TournamentAdminPage.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SPORT_OPTIONS = [
  'general', 'soccer', 'basketball', 'volleyball', 'softball',
  'hockey', 'football', 'pool', 'darts'
];

const FORMAT_OPTIONS = [
  { value: 'single_elim', label: 'Single Elimination' },
  { value: 'double_elim', label: 'Double Elimination' },
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'group_knockout', label: 'Group Stage → Knockout' },
];

const STATUS_ORDER = ['draft', 'registration_open', 'registration_closed', 'in_progress', 'completed'];

const STATUS_LABELS = {
  draft: 'Draft',
  registration_open: 'Registration Open',
  registration_closed: 'Reg. Closed',
  in_progress: 'In Progress',
  completed: 'Completed',
};

const STATUS_NEXT_LABELS = {
  draft: 'Open Registration',
  registration_open: 'Close Registration',
  registration_closed: 'Start Tournament',
  in_progress: 'Mark Complete',
};

const FORMAT_LABELS = {
  single_elim: 'Single Elimination',
  double_elim: 'Double Elimination',
  round_robin: 'Round Robin',
  group_knockout: 'Group Stage → Knockout',
};

const APP_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : window.location.origin;

// ---------------------------------------------------------------------------
// Helper: format local date string
// ---------------------------------------------------------------------------
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Bracket display helpers
// ---------------------------------------------------------------------------
function groupByRound(matches) {
  const rounds = {};
  for (const m of matches) {
    if (!rounds[m.roundNumber]) rounds[m.roundNumber] = [];
    rounds[m.roundNumber].push(m);
  }
  return rounds;
}

function groupByPool(matches) {
  const pools = {};
  for (const m of matches) {
    const key = m.pool || '__knockout__';
    if (!pools[key]) pools[key] = [];
    pools[key].push(m);
  }
  return pools;
}

// ---------------------------------------------------------------------------
// Sub-component: MatchScoreForm
// ---------------------------------------------------------------------------
function MatchScoreForm({ match, tournamentId, onScored, isDark }) {
  const [home, setHome] = useState(match.homeScore !== null && match.homeScore !== undefined ? String(match.homeScore) : '');
  const [away, setAway] = useState(match.awayScore !== null && match.awayScore !== undefined ? String(match.awayScore) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    if (home === '' || away === '') return setErr('Both scores required');
    try {
      setSaving(true);
      setErr('');
      await api.scoreTournamentMatch(tournamentId, match.id, Number(home), Number(away));
      onScored();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ta-score-form">
      {err && <div className="ta-score-err">{err}</div>}
      <div className="ta-score-inputs">
        <input
          type="number"
          min="0"
          value={home}
          onChange={e => setHome(e.target.value)}
          className="ta-score-input"
          placeholder="0"
          aria-label="Home score"
        />
        <span className="ta-score-vs">–</span>
        <input
          type="number"
          min="0"
          value={away}
          onChange={e => setAway(e.target.value)}
          className="ta-score-input"
          placeholder="0"
          aria-label="Away score"
        />
        <button className="ta-score-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: MatchRow (bracket row for a single match)
// ---------------------------------------------------------------------------
function MatchRow({ match, tournamentId, onScored }) {
  const [open, setOpen] = useState(false);

  const homeName = match.homeTeam?.teamName || (match.homeTeamId ? '?' : 'BYE');
  const awayName = match.awayTeam?.teamName || (match.awayTeamId ? '?' : 'BYE');
  const homeSeed = match.homeTeam?.seed;
  const awaySeed = match.awayTeam?.seed;
  const scored = match.status === 'completed';
  const isBye = !match.homeTeamId || !match.awayTeamId;
  const winnerName = match.winner?.teamName;

  return (
    <div className={`ta-match-row ${scored ? 'ta-match-scored' : ''} ${isBye ? 'ta-match-bye' : ''}`}>
      <div className="ta-match-teams">
        <span className={`ta-match-team ${match.winnerTeamId === match.homeTeamId && scored ? 'ta-match-winner' : ''}`}>
          {homeSeed != null ? <span className="ta-seed">#{homeSeed}</span> : null}{homeName}
        </span>
        <span className="ta-match-score-display">
          {scored ? `${match.homeScore} – ${match.awayScore}` : 'vs'}
        </span>
        <span className={`ta-match-team ta-match-team-away ${match.winnerTeamId === match.awayTeamId && scored ? 'ta-match-winner' : ''}`}>
          {awayName}{awaySeed != null ? <span className="ta-seed">#{awaySeed}</span> : null}
        </span>
      </div>
      {scored && winnerName && (
        <span className="ta-match-winner-label">Winner: {winnerName}</span>
      )}
      {!isBye && !scored && (
        <div className="ta-match-score-toggle">
          <button className="ta-score-toggle-btn" onClick={() => setOpen(o => !o)}>
            {open ? 'Cancel' : 'Enter Score'}
          </button>
          {open && (
            <MatchScoreForm
              match={match}
              tournamentId={tournamentId}
              onScored={() => { setOpen(false); onScored(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: SingleElimBracket / DoubleElimBracket view
// ---------------------------------------------------------------------------
function ElimBracket({ matches, tournamentId, onScored }) {
  const rounds = groupByRound(matches);
  const roundNums = Object.keys(rounds).map(Number).sort((a, b) => a - b);

  return (
    <div className="ta-bracket-elim">
      {roundNums.map(rn => (
        <div key={rn} className="ta-bracket-round">
          <div className="ta-round-label">{rounds[rn][0]?.roundName || `Round ${rn}`}</div>
          {rounds[rn].map(m => (
            <MatchRow key={m.id} match={m} tournamentId={tournamentId} onScored={onScored} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: RoundRobinSchedule
// ---------------------------------------------------------------------------
function RoundRobinSchedule({ matches, tournamentId, onScored }) {
  const rounds = groupByRound(matches.filter(m => m.status !== 'completed' || m.homeTeamId));
  const roundNums = Object.keys(rounds).map(Number).sort((a, b) => a - b);

  return (
    <div className="ta-rr-schedule">
      {roundNums.map(rn => (
        <div key={rn} className="ta-rr-round">
          <div className="ta-round-label">{rounds[rn][0]?.roundName || `Round ${rn}`}</div>
          {rounds[rn].filter(m => m.homeTeamId && m.awayTeamId).map(m => (
            <MatchRow key={m.id} match={m} tournamentId={tournamentId} onScored={onScored} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: StandingsTable
// ---------------------------------------------------------------------------
function StandingsTable({ standings }) {
  if (!standings || standings.length === 0) return <p className="ta-dim">No standings yet.</p>;
  return (
    <table className="ta-standings-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Team</th>
          <th>W</th>
          <th>D</th>
          <th>L</th>
          <th>GF</th>
          <th>GA</th>
          <th>GD</th>
          <th>Pts</th>
        </tr>
      </thead>
      <tbody>
        {standings.map((row, i) => (
          <tr key={row.teamId}>
            <td className="ta-dim">{i + 1}</td>
            <td><strong>{row.teamName}</strong></td>
            <td>{row.wins}</td>
            <td>{row.draws}</td>
            <td>{row.losses}</td>
            <td>{row.goalsFor}</td>
            <td>{row.goalsAgainst}</td>
            <td className={row.goalDiff >= 0 ? 'ta-pos' : 'ta-neg'}>{row.goalDiff >= 0 ? '+' : ''}{row.goalDiff}</td>
            <td><strong>{row.points}</strong></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: GroupKnockoutView
// ---------------------------------------------------------------------------
function GroupKnockoutView({ matches, tournamentId, standings, onScored, onAdvanceWinners, advancing }) {
  const byPool = groupByPool(matches);
  const poolKeys = Object.keys(byPool).filter(k => k !== '__knockout__').sort();
  const knockoutMatches = byPool['__knockout__'] || [];

  return (
    <div className="ta-gk-view">
      {/* Group phase */}
      {poolKeys.map(pool => (
        <div key={pool} className="ta-gk-pool">
          <h4 className="ta-pool-title">Pool {pool}</h4>
          {byPool[pool].filter(m => m.homeTeamId && m.awayTeamId).map(m => (
            <MatchRow key={m.id} match={m} tournamentId={tournamentId} onScored={onScored} />
          ))}
          {standings?.poolStandings?.[pool] && (
            <div className="ta-pool-standings">
              <div className="ta-pool-standings-title">Pool {pool} Standings</div>
              <StandingsTable standings={standings.poolStandings[pool]} />
            </div>
          )}
        </div>
      ))}

      {/* Advance group winners button */}
      {knockoutMatches.some(m => !m.homeTeamId || !m.awayTeamId) && (
        <div className="ta-advance-section">
          <button className="ta-advance-btn" onClick={onAdvanceWinners} disabled={advancing}>
            {advancing ? 'Advancing…' : 'Advance Group Winners to Knockout'}
          </button>
        </div>
      )}

      {/* Knockout phase */}
      {knockoutMatches.length > 0 && (
        <div className="ta-gk-knockout">
          <h4 className="ta-pool-title">Knockout Bracket</h4>
          <ElimBracket matches={knockoutMatches} tournamentId={tournamentId} onScored={onScored} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function TournamentAdminPage({ tournamentId, onBack }) {
  const { colors } = useTheme();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [tab, setTab] = useState('overview'); // overview | teams | bracket | qr | notify

  // Standings (loaded lazily for bracket tab)
  const [standings, setStandings] = useState(null);
  const [loadingStandings, setLoadingStandings] = useState(false);

  // Overview edit form
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Status advancement
  const [advancingStatus, setAdvancingStatus] = useState(false);

  // Teams tab state
  const [addTeamForm, setAddTeamForm] = useState({ teamName: '', captainName: '', captainEmail: '', captainPhone: '' });
  const [addingTeam, setAddingTeam] = useState(false);
  const [editTeamId, setEditTeamId] = useState(null);
  const [editTeamForm, setEditTeamForm] = useState({});
  const [savingTeam, setSavingTeam] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(null);
  const [seedMethod, setSeedMethod] = useState('registration_order');
  const [seeding, setSeeding] = useState(false);
  const [numPools, setNumPools] = useState(2);
  const [assigningPools, setAssigningPools] = useState(false);

  // Bracket tab state
  const [generating, setGenerating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [advancingGroupWinners, setAdvancingGroupWinners] = useState(false);

  // QR tab state
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Notifications tab state
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [announcementMsg, setAnnouncementMsg] = useState('');
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [announcementResult, setAnnouncementResult] = useState(null);

  // Check-in state
  const [checkingInTeam, setCheckingInTeam] = useState(null);
  const [checkingInAll, setCheckingInAll] = useState(false);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getTournament(tournamentId);
      setTournament(res.tournament);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => { load(); }, [load]);

  const loadStandings = useCallback(async () => {
    if (!tournament) return;
    if (!['round_robin', 'group_knockout'].includes(tournament.formatType)) return;
    try {
      setLoadingStandings(true);
      const res = await api.getTournamentStandings(tournamentId);
      setStandings(res);
    } catch {
      // Standings not available yet — that's OK
    } finally {
      setLoadingStandings(false);
    }
  }, [tournamentId, tournament]);

  useEffect(() => {
    if (tab === 'bracket') loadStandings();
  }, [tab, loadStandings]);

  // -------------------------------------------------------------------------
  // Overview: edit
  // -------------------------------------------------------------------------
  const startEdit = () => {
    if (!tournament) return;
    setEditForm({
      name: tournament.name || '',
      description: tournament.description || '',
      sport: tournament.sport || 'general',
      formatType: tournament.formatType || 'single_elim',
      startDate: tournament.startDate || '',
      endDate: tournament.endDate || '',
      registrationDeadline: tournament.registrationDeadline
        ? tournament.registrationDeadline.slice(0, 10)
        : '',
      maxTeams: tournament.maxTeams ?? '',
      minTeams: tournament.minTeams ?? '',
      entryFee: tournament.entryFee ?? '',
    });
    setEditing(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        name: editForm.name,
        description: editForm.description || undefined,
        sport: editForm.sport,
        formatType: editForm.formatType,
        startDate: editForm.startDate,
        endDate: editForm.endDate || undefined,
        registrationDeadline: editForm.registrationDeadline || undefined,
        maxTeams: editForm.maxTeams !== '' ? Number(editForm.maxTeams) : null,
        minTeams: editForm.minTeams !== '' ? Number(editForm.minTeams) : null,
        entryFee: editForm.entryFee !== '' ? Number(editForm.entryFee) : null,
      };
      await api.updateTournament(tournamentId, payload);
      setEditing(false);
      showToast('Saved!', 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Status advancement
  // -------------------------------------------------------------------------
  const handleAdvanceStatus = async () => {
    if (!tournament) return;
    const currentIdx = STATUS_ORDER.indexOf(tournament.status);
    const nextStatus = STATUS_ORDER[currentIdx + 1];
    if (!nextStatus) return;
    try {
      setAdvancingStatus(true);
      await api.updateTournamentStatus(tournamentId, nextStatus);
      showToast(`Status updated to: ${STATUS_LABELS[nextStatus]}`, 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setAdvancingStatus(false);
    }
  };

  // -------------------------------------------------------------------------
  // Teams tab
  // -------------------------------------------------------------------------
  const handleAddTeam = async (e) => {
    e.preventDefault();
    if (!addTeamForm.teamName.trim()) return showToast('Team name is required');
    try {
      setAddingTeam(true);
      await api.addTournamentTeam(tournamentId, {
        teamName: addTeamForm.teamName.trim(),
        captainName: addTeamForm.captainName || undefined,
        captainEmail: addTeamForm.captainEmail || undefined,
        captainPhone: addTeamForm.captainPhone || undefined,
      });
      setAddTeamForm({ teamName: '', captainName: '', captainEmail: '', captainPhone: '' });
      showToast('Team added', 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setAddingTeam(false);
    }
  };

  const startEditTeam = (team) => {
    setEditTeamId(team.id);
    setEditTeamForm({
      teamName: team.teamName || '',
      captainName: team.captainName || '',
      captainEmail: team.captainEmail || '',
      captainPhone: team.captainPhone || '',
      seed: team.seed ?? '',
      pool: team.pool || '',
    });
  };

  const handleSaveTeam = async (e) => {
    e.preventDefault();
    try {
      setSavingTeam(true);
      await api.updateTournamentTeam(tournamentId, editTeamId, {
        teamName: editTeamForm.teamName,
        captainName: editTeamForm.captainName || null,
        captainEmail: editTeamForm.captainEmail || null,
        captainPhone: editTeamForm.captainPhone || null,
        seed: editTeamForm.seed !== '' ? Number(editTeamForm.seed) : null,
        pool: editTeamForm.pool || null,
      });
      setEditTeamId(null);
      showToast('Team updated', 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSavingTeam(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      setDeletingTeam(teamId);
      await api.deleteTournamentTeam(tournamentId, teamId);
      showToast('Team removed', 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeletingTeam(null);
    }
  };

  const handleSeed = async () => {
    try {
      setSeeding(true);
      await api.seedTournamentTeams(tournamentId, seedMethod);
      showToast('Teams seeded!', 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSeeding(false);
    }
  };

  const handleAssignPools = async () => {
    try {
      setAssigningPools(true);
      await api.assignTournamentPools(tournamentId, Number(numPools));
      showToast('Pools assigned!', 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setAssigningPools(false);
    }
  };

  // -------------------------------------------------------------------------
  // Bracket tab
  // -------------------------------------------------------------------------
  const handleGenerateBracket = async () => {
    try {
      setGenerating(true);
      const res = await api.generateTournamentBracket(tournamentId);
      showToast(`Bracket generated — ${res.matchCount} matches`, 'success');
      await load();
      await loadStandings();
    } catch (err) {
      showToast(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleResetBracket = async () => {
    try {
      setResetting(true);
      await api.resetTournamentBracket(tournamentId);
      setConfirmReset(false);
      setStandings(null);
      showToast('Bracket reset', 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setResetting(false);
    }
  };

  const handleAdvanceGroupWinners = async () => {
    try {
      setAdvancingGroupWinners(true);
      await api.advanceTournamentGroupWinners(tournamentId);
      showToast('Group winners advanced!', 'success');
      await load();
      await loadStandings();
    } catch (err) {
      showToast(err.message);
    } finally {
      setAdvancingGroupWinners(false);
    }
  };

  // -------------------------------------------------------------------------
  // Notifications tab
  // -------------------------------------------------------------------------
  const handleSendReminder = async () => {
    try {
      setSendingReminder(true);
      setReminderResult(null);
      const res = await api.sendTournamentReminder(tournamentId);
      setReminderResult({ type: 'success', sent: res.sent, failed: res.failed, skipped: res.skipped });
    } catch (err) {
      setReminderResult({ type: 'error', message: err.message });
    } finally {
      setSendingReminder(false);
    }
  };

  const handleSendAnnouncement = async () => {
    if (!announcementMsg.trim()) return;
    try {
      setSendingAnnouncement(true);
      setAnnouncementResult(null);
      const res = await api.sendTournamentAnnouncement(tournamentId, announcementMsg);
      setAnnouncementResult({ type: 'success', sent: res.sent, failed: res.failed, skipped: res.skipped });
      setAnnouncementMsg('');
    } catch (err) {
      setAnnouncementResult({ type: 'error', message: err.message });
    } finally {
      setSendingAnnouncement(false);
    }
  };

  // -------------------------------------------------------------------------
  // Check-in handlers
  // -------------------------------------------------------------------------
  const handleCheckInTeam = async (teamId) => {
    try {
      setCheckingInTeam(teamId);
      await api.checkInTournamentTeam(tournamentId, teamId);
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setCheckingInTeam(null);
    }
  };

  const handleCheckInAll = async () => {
    try {
      setCheckingInAll(true);
      const res = await api.checkInAllTournamentTeams(tournamentId);
      showToast(`${res.checkedIn} team${res.checkedIn !== 1 ? 's' : ''} checked in`, 'success');
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setCheckingInAll(false);
    }
  };

  // -------------------------------------------------------------------------
  // QR tab
  // -------------------------------------------------------------------------
  const joinUrl = tournament ? `${APP_URL}/join/t/${tournament.joinSlug}` : '';

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(joinUrl).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };

  const handleDownloadPng = () => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_URL || '/api'}/tournaments/${tournamentId}/qr/png`;
    // Open in new tab — browser will handle download via Content-Disposition header
    const a = document.createElement('a');
    a.href = url + (token ? `?token=${encodeURIComponent(token)}` : '');
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  const handleOpenFlyer = () => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_URL || '/api'}/tournaments/${tournamentId}/flyer`;
    window.open(url + (token ? `?token=${encodeURIComponent(token)}` : ''), '_blank', 'noopener,noreferrer');
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) return <div className="ta-page" style={{ background: colors.bg, color: colors.text }}><div className="ta-center ta-dim">Loading…</div></div>;
  if (error) return <div className="ta-page" style={{ background: colors.bg, color: colors.text }}><div className="ta-center ta-error">{error}</div></div>;
  if (!tournament) return null;

  const teams = tournament.teams || [];
  const matches = tournament.matches || [];
  const hasMatches = matches.length > 0;
  const currentStatusIdx = STATUS_ORDER.indexOf(tournament.status);
  const nextStatus = STATUS_ORDER[currentStatusIdx + 1];
  const isCompleted = tournament.status === 'completed';

  return (
    <div className="ta-page" style={{ background: colors.bg, color: colors.text }}>
      {toast && <div className={`ta-toast ta-toast-${toast.type}`}>{toast.msg}</div>}

      {/* ---- Header ---- */}
      <div className="ta-header">
        <button className="ta-back-btn" onClick={onBack}>← Tournaments</button>
        <div className="ta-header-info">
          <div className="ta-header-top">
            <h1 className="ta-title">{tournament.name}</h1>
            <span className={`ta-status-badge ta-status-${tournament.status.replace('_', '-')}`}>
              {STATUS_LABELS[tournament.status]}
            </span>
          </div>
          <div className="ta-header-meta">
            <span>{FORMAT_LABELS[tournament.formatType]}</span>
            <span className="ta-sep">·</span>
            <span className="ta-sport">{tournament.sport}</span>
            <span className="ta-sep">·</span>
            <span>{teams.length} team{teams.length !== 1 ? 's' : ''}</span>
            {tournament.maxTeams && <><span className="ta-sep">·</span><span>max {tournament.maxTeams}</span></>}
            <span className="ta-sep">·</span>
            <span>{fmtDate(tournament.startDate)}{tournament.endDate && tournament.endDate !== tournament.startDate ? ` – ${fmtDate(tournament.endDate)}` : ''}</span>
          </div>
        </div>
        {nextStatus && !isCompleted && (
          <button className="ta-status-advance-btn" onClick={handleAdvanceStatus} disabled={advancingStatus}>
            {advancingStatus ? 'Updating…' : STATUS_NEXT_LABELS[tournament.status]}
          </button>
        )}
      </div>

      {/* ---- Tabs ---- */}
      <div className="ta-tabs">
        {['overview', 'teams', 'bracket', 'qr', 'notify'].map(t => (
          <button
            key={t}
            className={`ta-tab ${tab === t ? 'ta-tab-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'overview' ? 'Overview'
              : t === 'teams' ? `Teams (${teams.length})`
              : t === 'bracket' ? `Bracket (${matches.length})`
              : t === 'qr' ? 'QR Code'
              : 'Notifications'}
          </button>
        ))}
      </div>

      <div className="ta-body" style={{ background: colors.card }}>

        {/* ================================================================
            OVERVIEW TAB
        ================================================================= */}
        {tab === 'overview' && (
          <div className="ta-overview">
            {!editing ? (
              <>
                <div className="ta-info-grid">
                  <div className="ta-info-row"><span className="ta-info-label">Name</span><span>{tournament.name}</span></div>
                  <div className="ta-info-row"><span className="ta-info-label">Sport</span><span>{tournament.sport}</span></div>
                  <div className="ta-info-row"><span className="ta-info-label">Format</span><span>{FORMAT_LABELS[tournament.formatType]}</span></div>
                  <div className="ta-info-row"><span className="ta-info-label">Start Date</span><span>{fmtDate(tournament.startDate)}</span></div>
                  {tournament.endDate && <div className="ta-info-row"><span className="ta-info-label">End Date</span><span>{fmtDate(tournament.endDate)}</span></div>}
                  {tournament.registrationDeadline && <div className="ta-info-row"><span className="ta-info-label">Reg. Deadline</span><span>{new Date(tournament.registrationDeadline).toLocaleDateString()}</span></div>}
                  {tournament.maxTeams && <div className="ta-info-row"><span className="ta-info-label">Max Teams</span><span>{tournament.maxTeams}</span></div>}
                  {tournament.minTeams && <div className="ta-info-row"><span className="ta-info-label">Min Teams</span><span>{tournament.minTeams}</span></div>}
                  {tournament.entryFee && <div className="ta-info-row"><span className="ta-info-label">Entry Fee</span><span>${parseFloat(tournament.entryFee).toFixed(2)}</span></div>}
                  {tournament.description && <div className="ta-info-row ta-info-row-full"><span className="ta-info-label">Description</span><span>{tournament.description}</span></div>}
                </div>
                <div className="ta-overview-join">
                  <span className="ta-info-label">Join URL</span>
                  <div className="ta-join-url-row">
                    <span className="ta-join-url">{joinUrl}</span>
                    <button className="ta-copy-btn" onClick={handleCopyUrl}>{copiedUrl ? 'Copied!' : 'Copy'}</button>
                  </div>
                  {tournament?.joinSlug && (
                    <a
                      href={`/t/${tournament.joinSlug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.8rem', color: '#60a5fa', textDecoration: 'none', marginTop: 6, display: 'inline-block' }}
                    >
                      View public tournament page →
                    </a>
                  )}
                </div>
                <button className="ta-edit-btn" onClick={startEdit}>Edit Settings</button>
              </>
            ) : (
              <form className="ta-edit-form" onSubmit={handleSaveEdit}>
                <div className="ta-form-row">
                  <label>Name *</label>
                  <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="ta-form-row-2">
                  <div className="ta-form-row">
                    <label>Sport</label>
                    <select value={editForm.sport} onChange={e => setEditForm(f => ({ ...f, sport: e.target.value }))}>
                      {SPORT_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="ta-form-row">
                    <label>Format</label>
                    <select value={editForm.formatType} onChange={e => setEditForm(f => ({ ...f, formatType: e.target.value }))}>
                      {FORMAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="ta-form-row-2">
                  <div className="ta-form-row">
                    <label>Start Date *</label>
                    <input type="date" value={editForm.startDate} onChange={e => setEditForm(f => ({ ...f, startDate: e.target.value }))} required />
                  </div>
                  <div className="ta-form-row">
                    <label>End Date</label>
                    <input type="date" value={editForm.endDate} onChange={e => setEditForm(f => ({ ...f, endDate: e.target.value }))} />
                  </div>
                </div>
                <div className="ta-form-row-2">
                  <div className="ta-form-row">
                    <label>Reg. Deadline</label>
                    <input type="date" value={editForm.registrationDeadline} onChange={e => setEditForm(f => ({ ...f, registrationDeadline: e.target.value }))} />
                  </div>
                  <div className="ta-form-row">
                    <label>Entry Fee ($)</label>
                    <input type="number" min="0" step="0.01" value={editForm.entryFee} onChange={e => setEditForm(f => ({ ...f, entryFee: e.target.value }))} placeholder="0.00" />
                  </div>
                </div>
                <div className="ta-form-row-2">
                  <div className="ta-form-row">
                    <label>Min Teams</label>
                    <input type="number" min="2" value={editForm.minTeams} onChange={e => setEditForm(f => ({ ...f, minTeams: e.target.value }))} />
                  </div>
                  <div className="ta-form-row">
                    <label>Max Teams</label>
                    <input type="number" min="2" value={editForm.maxTeams} onChange={e => setEditForm(f => ({ ...f, maxTeams: e.target.value }))} />
                  </div>
                </div>
                <div className="ta-form-row">
                  <label>Description</label>
                  <textarea rows={3} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="ta-edit-actions">
                  <button type="button" className="ta-cancel-btn" onClick={() => setEditing(false)}>Cancel</button>
                  <button type="submit" className="ta-save-btn" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* ================================================================
            TEAMS TAB
        ================================================================= */}
        {tab === 'teams' && (
          <div className="ta-teams">
            {/* Check-in bar */}
            {teams.length > 0 && (() => {
              const checkedIn = teams.filter(t => t.status === 'checked_in').length;
              const checkable = teams.filter(t => ['registered', 'confirmed'].includes(t.status)).length;
              return (
                <div className="ta-checkin-bar">
                  <span className="ta-checkin-count">
                    {checkedIn} / {teams.length} checked in
                  </span>
                  {checkable > 0 && (
                    <button className="ta-checkin-all-btn" onClick={handleCheckInAll} disabled={checkingInAll}>
                      {checkingInAll ? 'Checking in…' : `Check In All (${checkable})`}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Seed + Pool tools */}
            <div className="ta-team-tools">
              <div className="ta-tool-group">
                <label className="ta-tool-label">Seed teams:</label>
                <select value={seedMethod} onChange={e => setSeedMethod(e.target.value)} className="ta-tool-select">
                  <option value="registration_order">Registration Order</option>
                  <option value="random">Random</option>
                </select>
                <button className="ta-tool-btn" onClick={handleSeed} disabled={seeding || teams.length === 0}>
                  {seeding ? 'Seeding…' : 'Auto-Seed'}
                </button>
              </div>
              {tournament.formatType === 'group_knockout' && (
                <div className="ta-tool-group">
                  <label className="ta-tool-label">Assign pools:</label>
                  <input
                    type="number"
                    min="2"
                    max="8"
                    value={numPools}
                    onChange={e => setNumPools(e.target.value)}
                    className="ta-tool-num"
                  />
                  <span className="ta-tool-label">pools</span>
                  <button className="ta-tool-btn" onClick={handleAssignPools} disabled={assigningPools || teams.length < 2}>
                    {assigningPools ? 'Assigning…' : 'Assign Pools'}
                  </button>
                </div>
              )}
            </div>

            {/* Team table */}
            {teams.length === 0 ? (
              <p className="ta-dim">No teams yet. Add teams below.</p>
            ) : (
              <div className="ta-teams-table-wrap">
                <table className="ta-teams-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Team</th>
                      <th>Captain</th>
                      {tournament.formatType === 'group_knockout' && <th>Pool</th>}
                      <th>Status</th>
                      <th>Source</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team, i) => (
                      <React.Fragment key={team.id}>
                        <tr>
                          <td className="ta-dim">{team.seed ?? i + 1}</td>
                          <td><strong>{team.teamName}</strong></td>
                          <td className="ta-dim">
                            {team.captainName || '—'}
                            {team.captainEmail && <div className="ta-captain-email">{team.captainEmail}</div>}
                          </td>
                          {tournament.formatType === 'group_knockout' && (
                            <td>{team.pool ? <span className="ta-pool-badge">Pool {team.pool}</span> : <span className="ta-dim">—</span>}</td>
                          )}
                          <td>
                            <span className={`ta-team-status ta-team-status-${team.status}`}>{team.status}</span>
                          </td>
                          <td className="ta-dim">{team.registrationMethod || 'manual'}</td>
                          <td className="ta-team-actions">
                            {['registered', 'confirmed'].includes(team.status) && (
                              <button
                                className="ta-checkin-btn"
                                onClick={() => handleCheckInTeam(team.id)}
                                disabled={checkingInTeam === team.id}
                                title="Check in"
                              >{checkingInTeam === team.id ? '…' : 'Check In'}</button>
                            )}
                            <button className="ta-icon-btn" onClick={() => startEditTeam(team)} title="Edit">✏️</button>
                            <button
                              className="ta-icon-btn ta-icon-del"
                              onClick={() => handleDeleteTeam(team.id)}
                              disabled={deletingTeam === team.id}
                              title="Remove"
                            >{deletingTeam === team.id ? '…' : '🗑'}</button>
                          </td>
                        </tr>
                        {editTeamId === team.id && (
                          <tr>
                            <td colSpan={tournament.formatType === 'group_knockout' ? 7 : 6} className="ta-edit-team-row">
                              <form className="ta-edit-team-form" onSubmit={handleSaveTeam}>
                                <div className="ta-form-row-2">
                                  <div className="ta-form-row"><label>Name</label>
                                    <input value={editTeamForm.teamName} onChange={e => setEditTeamForm(f => ({ ...f, teamName: e.target.value }))} required /></div>
                                  <div className="ta-form-row"><label>Seed</label>
                                    <input type="number" min="1" value={editTeamForm.seed} onChange={e => setEditTeamForm(f => ({ ...f, seed: e.target.value }))} /></div>
                                </div>
                                {tournament.formatType === 'group_knockout' && (
                                  <div className="ta-form-row"><label>Pool</label>
                                    <input value={editTeamForm.pool} onChange={e => setEditTeamForm(f => ({ ...f, pool: e.target.value }))} placeholder="A" maxLength={2} /></div>
                                )}
                                <div className="ta-form-row-2">
                                  <div className="ta-form-row"><label>Captain</label>
                                    <input value={editTeamForm.captainName} onChange={e => setEditTeamForm(f => ({ ...f, captainName: e.target.value }))} /></div>
                                  <div className="ta-form-row"><label>Captain Email</label>
                                    <input type="email" value={editTeamForm.captainEmail} onChange={e => setEditTeamForm(f => ({ ...f, captainEmail: e.target.value }))} /></div>
                                </div>
                                <div className="ta-edit-actions">
                                  <button type="button" className="ta-cancel-btn" onClick={() => setEditTeamId(null)}>Cancel</button>
                                  <button type="submit" className="ta-save-btn" disabled={savingTeam}>{savingTeam ? 'Saving…' : 'Save'}</button>
                                </div>
                              </form>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add team form */}
            <div className="ta-add-team-section">
              <h3 className="ta-section-title">Add Team</h3>
              <form className="ta-add-team-form" onSubmit={handleAddTeam}>
                <div className="ta-form-row-2">
                  <div className="ta-form-row"><label>Team Name *</label>
                    <input value={addTeamForm.teamName} onChange={e => setAddTeamForm(f => ({ ...f, teamName: e.target.value }))} placeholder="Thunder Hawks" required /></div>
                  <div className="ta-form-row"><label>Captain Name</label>
                    <input value={addTeamForm.captainName} onChange={e => setAddTeamForm(f => ({ ...f, captainName: e.target.value }))} placeholder="Jane Smith" /></div>
                </div>
                <div className="ta-form-row-2">
                  <div className="ta-form-row"><label>Captain Email</label>
                    <input type="email" value={addTeamForm.captainEmail} onChange={e => setAddTeamForm(f => ({ ...f, captainEmail: e.target.value }))} placeholder="jane@example.com" /></div>
                  <div className="ta-form-row"><label>Captain Phone</label>
                    <input value={addTeamForm.captainPhone} onChange={e => setAddTeamForm(f => ({ ...f, captainPhone: e.target.value }))} placeholder="555-1234" /></div>
                </div>
                <button type="submit" className="ta-add-team-btn" disabled={addingTeam}>
                  {addingTeam ? 'Adding…' : '+ Add Team'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ================================================================
            BRACKET TAB
        ================================================================= */}
        {tab === 'bracket' && (
          <div className="ta-bracket">
            {!hasMatches ? (
              <div className="ta-bracket-empty">
                <p className="ta-dim">No bracket generated yet.</p>
                {tournament.status !== 'registration_closed' && tournament.status !== 'in_progress' ? (
                  <p className="ta-hint">Close registration before generating a bracket.</p>
                ) : teams.length < 2 ? (
                  <p className="ta-hint">Add at least 2 teams before generating.</p>
                ) : (
                  <button className="ta-generate-btn" onClick={handleGenerateBracket} disabled={generating}>
                    {generating ? 'Generating…' : `Generate ${FORMAT_LABELS[tournament.formatType]} Bracket`}
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="ta-bracket-toolbar">
                  <span className="ta-dim">{matches.length} match{matches.length !== 1 ? 'es' : ''}</span>
                  <button className="ta-reset-btn" onClick={() => setConfirmReset(true)}>Reset Bracket</button>
                </div>

                {/* Bracket views */}
                {(tournament.formatType === 'single_elim' || tournament.formatType === 'double_elim') && (
                  <ElimBracket matches={matches} tournamentId={tournamentId} onScored={() => { load(); loadStandings(); }} />
                )}

                {tournament.formatType === 'round_robin' && (
                  <>
                    <RoundRobinSchedule matches={matches} tournamentId={tournamentId} onScored={() => { load(); loadStandings(); }} />
                    {loadingStandings ? (
                      <p className="ta-dim">Loading standings…</p>
                    ) : standings?.standings ? (
                      <div className="ta-standings-section">
                        <h3 className="ta-section-title">Standings</h3>
                        <StandingsTable standings={standings.standings} />
                      </div>
                    ) : null}
                  </>
                )}

                {tournament.formatType === 'group_knockout' && (
                  <GroupKnockoutView
                    matches={matches}
                    tournamentId={tournamentId}
                    standings={standings}
                    onScored={() => { load(); loadStandings(); }}
                    onAdvanceWinners={handleAdvanceGroupWinners}
                    advancing={advancingGroupWinners}
                  />
                )}
              </>
            )}

            {/* Reset confirm */}
            {confirmReset && (
              <div className="ta-modal-overlay" onClick={() => setConfirmReset(false)}>
                <div className="ta-modal" onClick={e => e.stopPropagation()} style={{ background: colors.card }}>
                  <h2 className="ta-modal-title">Reset Bracket?</h2>
                  <p className="ta-modal-body">This will delete all {matches.length} matches and reset team statuses. Scores cannot be recovered.</p>
                  <div className="ta-modal-actions">
                    <button className="ta-cancel-btn" onClick={() => setConfirmReset(false)}>Cancel</button>
                    <button className="ta-danger-btn" onClick={handleResetBracket} disabled={resetting}>
                      {resetting ? 'Resetting…' : 'Reset'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ================================================================
            NOTIFICATIONS TAB
        ================================================================= */}
        {tab === 'notify' && (
          <div className="ta-notify">
            {/* Check-in summary */}
            {teams.length > 0 && (() => {
              const checkedIn = teams.filter(t => t.status === 'checked_in').length;
              const withEmail = teams.filter(t => t.captainEmail).length;
              return (
                <div className="ta-notify-summary">
                  <div className="ta-notify-stat">
                    <span className="ta-notify-stat-num">{checkedIn}</span>
                    <span className="ta-notify-stat-label">checked in</span>
                  </div>
                  <div className="ta-notify-stat">
                    <span className="ta-notify-stat-num">{withEmail}</span>
                    <span className="ta-notify-stat-label">teams with email</span>
                  </div>
                  <div className="ta-notify-stat">
                    <span className="ta-notify-stat-num">{teams.length}</span>
                    <span className="ta-notify-stat-label">total teams</span>
                  </div>
                </div>
              );
            })()}

            {/* Day-of Reminder */}
            <div className="ta-notify-section">
              <h3 className="ta-section-title">Day-Of Reminder</h3>
              <p className="ta-notify-desc">
                Send a day-of reminder to all team captains who have an email address. Includes tournament name, date, venue, and a link to the live bracket.
              </p>
              {reminderResult && (
                <div className={`ta-notify-result ta-notify-result-${reminderResult.type}`}>
                  {reminderResult.type === 'success'
                    ? `Sent ${reminderResult.sent} reminder${reminderResult.sent !== 1 ? 's' : ''}${reminderResult.failed ? ` (${reminderResult.failed} failed)` : ''}${reminderResult.skipped ? `, ${reminderResult.skipped} skipped (no email)` : ''}.`
                    : reminderResult.message}
                </div>
              )}
              <button
                className="ta-notify-send-btn"
                onClick={handleSendReminder}
                disabled={sendingReminder || teams.filter(t => t.captainEmail).length === 0}
              >
                {sendingReminder ? 'Sending…' : `Send Day-Of Reminder (${teams.filter(t => t.captainEmail).length} email${teams.filter(t => t.captainEmail).length !== 1 ? 's' : ''})`}
              </button>
              {teams.filter(t => t.captainEmail).length === 0 && (
                <p className="ta-hint">No team captains have an email address on file.</p>
              )}
            </div>

            {/* Custom Announcement */}
            <div className="ta-notify-section">
              <h3 className="ta-section-title">Custom Announcement</h3>
              <p className="ta-notify-desc">
                Broadcast a message to all team captains. Use this for schedule changes, location updates, or any day-of communications.
              </p>
              <textarea
                className="ta-notify-textarea"
                rows={4}
                placeholder="e.g. Court assignments have changed — Pool A will now play on Court 3..."
                value={announcementMsg}
                onChange={e => setAnnouncementMsg(e.target.value)}
                maxLength={2000}
              />
              <div className="ta-notify-textarea-meta">
                <span className="ta-dim">{announcementMsg.length} / 2000</span>
              </div>
              {announcementResult && (
                <div className={`ta-notify-result ta-notify-result-${announcementResult.type}`}>
                  {announcementResult.type === 'success'
                    ? `Sent ${announcementResult.sent} announcement${announcementResult.sent !== 1 ? 's' : ''}${announcementResult.failed ? ` (${announcementResult.failed} failed)` : ''}${announcementResult.skipped ? `, ${announcementResult.skipped} skipped (no email)` : ''}.`
                    : announcementResult.message}
                </div>
              )}
              <button
                className="ta-notify-send-btn"
                onClick={handleSendAnnouncement}
                disabled={sendingAnnouncement || !announcementMsg.trim() || teams.filter(t => t.captainEmail).length === 0}
              >
                {sendingAnnouncement ? 'Sending…' : 'Send Announcement'}
              </button>
            </div>

            {/* Auto-notifications info */}
            <div className="ta-notify-section ta-notify-auto">
              <h3 className="ta-section-title">Automatic Notifications</h3>
              <ul className="ta-notify-auto-list">
                <li>
                  <span className="ta-notify-auto-icon">✓</span>
                  <div>
                    <strong>Match ready</strong> — Sent to both captains automatically when they advance to the next round.
                  </div>
                </li>
                <li>
                  <span className="ta-notify-auto-icon">✓</span>
                  <div>
                    <strong>Tournament complete</strong> — Sent to all captains when the tournament ends, announcing the champion.
                  </div>
                </li>
              </ul>
              <p className="ta-dim" style={{ fontSize: '0.8rem', marginTop: 8 }}>
                Automatic notifications are sent only to captains with an email address on file.
              </p>
            </div>
          </div>
        )}

        {/* ================================================================
            QR CODE TAB
        ================================================================= */}
        {tab === 'qr' && (
          <div className="ta-qr">
            <div className="ta-qr-join-url">
              <div className="ta-info-label">Registration URL</div>
              <div className="ta-join-url-row">
                <span className="ta-join-url">{joinUrl}</span>
                <button className="ta-copy-btn" onClick={handleCopyUrl}>{copiedUrl ? 'Copied!' : 'Copy'}</button>
              </div>
            </div>

            {tournament.qrCodeSvg ? (
              <div
                className="ta-qr-svg-wrap"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tournament.qrCodeSvg, { USE_PROFILES: { svg: true } }) }}
              />
            ) : (
              <div className="ta-dim ta-center">QR code not available — try regenerating.</div>
            )}

            <div className="ta-qr-actions">
              <button className="ta-qr-btn" onClick={handleDownloadPng}>Download PNG</button>
              <button className="ta-qr-btn ta-qr-btn-outline" onClick={handleOpenFlyer}>Open Printable Flyer</button>
            </div>

            <div className="ta-qr-hint">
              Share this QR code or link so teams can self-register for <strong>{tournament.name}</strong>.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TournamentAdminPage;
